import type { DocumentFilters } from "@/services/documentsApi";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Filter types available in the filter system
 */
export type FilterType = "proNumber" | "shipmentId" | "documentType" | "dateRange";

/**
 * Date range filter value
 */
export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
  preset?: "today" | "last7days" | "last30days" | "custom";
}

/**
 * Active filter representation
 */
export interface ActiveFilter {
  type: FilterType;
  value: string | string[] | DateRange;
  displayValue: string;
}

/**
 * Return type for useFilters hook
 */
export interface UseFiltersReturn {
  /** Current list of active filters */
  filters: ActiveFilter[];
  /** Add a new filter (replaces existing filter of same type) */
  addFilter: (filter: ActiveFilter) => void;
  /** Remove a filter by type */
  removeFilter: (filterType: FilterType) => void;
  /** Update an existing filter's value */
  updateFilter: (filterType: FilterType, value: string | string[] | DateRange) => void;
  /** Clear all active filters */
  clearFilters: () => void;
  /** Build DocumentFilters object from active filters for API calls */
  buildQueryParams: () => DocumentFilters;
}

/**
 * Options for useFilters hook
 */
export interface UseFiltersOptions {
  /** Whether to default to "today" date filter when no filters exist. Default: false */
  defaultToToday?: boolean;
}

// URL parameter keys for filters
const URL_PARAM_KEYS = {
  proNumber: "proNumber",
  shipmentId: "shipmentId",
  documentType: "docTypes",
  dateRangeStart: "startDate",
  dateRangeEnd: "endDate",
  dateRangePreset: "datePreset",
  dateRangeActive: "dateFilter", // Marker to indicate date filter is active
} as const;

/**
 * Format a date for display
 */
function formatDateForDisplay(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a date range for display
 */
function formatDateRangeDisplay(dateRange: DateRange): string {
  if (dateRange.preset && dateRange.preset !== "custom") {
    switch (dateRange.preset) {
      case "today":
        return "Today";
      case "last7days":
        return "Last 7 days";
      case "last30days":
        return "Last 30 days";
    }
  }

  const parts: string[] = [];
  if (dateRange.startDate) {
    parts.push(formatDateForDisplay(dateRange.startDate));
  }
  if (dateRange.endDate) {
    parts.push(formatDateForDisplay(dateRange.endDate));
  }
  return parts.join(" - ");
}

/**
 * Parse filters from URL search params
 */
function parseFiltersFromUrl(searchParams: URLSearchParams): ActiveFilter[] {
  const filters: ActiveFilter[] = [];

  // Parse PRO number (check if param exists, not just if it has value)
  if (searchParams.has(URL_PARAM_KEYS.proNumber)) {
    const proNumber = searchParams.get(URL_PARAM_KEYS.proNumber) || "";
    filters.push({
      type: "proNumber",
      value: proNumber,
      displayValue: proNumber || "All",
    });
  }

  // Parse shipment ID (check if param exists, not just if it has value)
  if (searchParams.has(URL_PARAM_KEYS.shipmentId)) {
    const shipmentId = searchParams.get(URL_PARAM_KEYS.shipmentId) || "";
    filters.push({
      type: "shipmentId",
      value: shipmentId,
      displayValue: shipmentId || "All",
    });
  }

  // Parse document types (comma-separated)
  if (searchParams.has(URL_PARAM_KEYS.documentType)) {
    const docTypes = searchParams.get(URL_PARAM_KEYS.documentType) || "";
    const types = docTypes.split(",").filter(Boolean);
    filters.push({
      type: "documentType",
      value: types,
      displayValue:
        types.length === 0 ? "All" : types.length === 1 ? types[0] : `${types.length} types`,
    });
  }

  // Parse date range (check for marker or any date params)
  const hasDateFilter = searchParams.has(URL_PARAM_KEYS.dateRangeActive);
  const startDateStr = searchParams.get(URL_PARAM_KEYS.dateRangeStart);
  const endDateStr = searchParams.get(URL_PARAM_KEYS.dateRangeEnd);
  const preset = searchParams.get(URL_PARAM_KEYS.dateRangePreset) as DateRange["preset"] | null;

  if (hasDateFilter || startDateStr || endDateStr || preset) {
    const dateRange: DateRange = {
      startDate: startDateStr ? new Date(startDateStr) : null,
      endDate: endDateStr ? new Date(endDateStr) : null,
      preset: preset || "custom",
    };
    const displayValue =
      dateRange.startDate || dateRange.endDate ? formatDateRangeDisplay(dateRange) : "All";
    filters.push({
      type: "dateRange",
      value: dateRange,
      displayValue,
    });
  }

  return filters;
}

/**
 * Hook to manage document filter state in URL search params
 *
 * Provides functions to add, remove, update, and clear filters.
 * Builds DocumentFilters object for API calls.
 * Persists filters in URL for navigation preservation.
 *
 * @param options - Configuration options
 * @returns Filter state and management functions
 */
export function useFilters(options?: UseFiltersOptions): UseFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const { defaultToToday = false } = options ?? {};
  const hasInitialized = useRef(false);

  // Derive filters from URL (single source of truth)
  const filters = useMemo(() => parseFiltersFromUrl(searchParams), [searchParams]);

  // Effect to set default "today" filter on initial load if no filters exist
  useEffect(() => {
    if (!defaultToToday || hasInitialized.current) {
      return;
    }

    // Check if there are any existing filter params in URL
    const hasExistingFilters =
      searchParams.has(URL_PARAM_KEYS.proNumber) ||
      searchParams.has(URL_PARAM_KEYS.shipmentId) ||
      searchParams.has(URL_PARAM_KEYS.documentType) ||
      searchParams.has(URL_PARAM_KEYS.dateRangeActive) ||
      searchParams.has(URL_PARAM_KEYS.dateRangeStart) ||
      searchParams.has(URL_PARAM_KEYS.dateRangeEnd) ||
      searchParams.has(URL_PARAM_KEYS.dateRangePreset);

    if (!hasExistingFilters) {
      // Set default "today" filter using existing helper
      const todayRange = createDateRangeFromPreset("today");
      const { startDate, endDate } = todayRange;

      // Guard against null (shouldn't happen for "today" preset, but satisfies TypeScript)
      if (!startDate || !endDate) {
        hasInitialized.current = true;
        return;
      }

      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(URL_PARAM_KEYS.dateRangeActive, "1");
          params.set(URL_PARAM_KEYS.dateRangeStart, startDate.toISOString());
          params.set(URL_PARAM_KEYS.dateRangeEnd, endDate.toISOString());
          params.set(URL_PARAM_KEYS.dateRangePreset, "today");
          return params;
        },
        { replace: true }
      );
    }

    hasInitialized.current = true;
  }, [defaultToToday, searchParams, setSearchParams]);

  const addFilter = useCallback(
    (filter: ActiveFilter) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);

          // Set URL params based on filter type
          switch (filter.type) {
            case "proNumber":
              if (typeof filter.value === "string") {
                params.set(URL_PARAM_KEYS.proNumber, filter.value);
              }
              break;
            case "shipmentId":
              if (typeof filter.value === "string") {
                params.set(URL_PARAM_KEYS.shipmentId, filter.value);
              }
              break;
            case "documentType":
              if (Array.isArray(filter.value)) {
                params.set(URL_PARAM_KEYS.documentType, filter.value.join(","));
              }
              break;
            case "dateRange":
              // Always set the marker to indicate date filter is active
              params.set(URL_PARAM_KEYS.dateRangeActive, "1");
              if (typeof filter.value === "object" && !Array.isArray(filter.value)) {
                const dateRange = filter.value as DateRange;
                if (dateRange.startDate) {
                  params.set(URL_PARAM_KEYS.dateRangeStart, dateRange.startDate.toISOString());
                } else {
                  params.delete(URL_PARAM_KEYS.dateRangeStart);
                }
                if (dateRange.endDate) {
                  params.set(URL_PARAM_KEYS.dateRangeEnd, dateRange.endDate.toISOString());
                } else {
                  params.delete(URL_PARAM_KEYS.dateRangeEnd);
                }
                if (dateRange.preset) {
                  params.set(URL_PARAM_KEYS.dateRangePreset, dateRange.preset);
                } else {
                  params.delete(URL_PARAM_KEYS.dateRangePreset);
                }
              }
              break;
          }

          // Reset to page 1 when filters change
          params.delete("page");

          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const removeFilter = useCallback(
    (filterType: FilterType) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);

          switch (filterType) {
            case "proNumber":
              params.delete(URL_PARAM_KEYS.proNumber);
              break;
            case "shipmentId":
              params.delete(URL_PARAM_KEYS.shipmentId);
              break;
            case "documentType":
              params.delete(URL_PARAM_KEYS.documentType);
              break;
            case "dateRange":
              params.delete(URL_PARAM_KEYS.dateRangeActive);
              params.delete(URL_PARAM_KEYS.dateRangeStart);
              params.delete(URL_PARAM_KEYS.dateRangeEnd);
              params.delete(URL_PARAM_KEYS.dateRangePreset);
              break;
          }

          // Reset to page 1 when filters change
          params.delete("page");

          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const updateFilter = useCallback(
    (filterType: FilterType, value: string | string[] | DateRange) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);

          switch (filterType) {
            case "proNumber":
              if (typeof value === "string") {
                params.set(URL_PARAM_KEYS.proNumber, value);
              }
              break;
            case "shipmentId":
              if (typeof value === "string") {
                params.set(URL_PARAM_KEYS.shipmentId, value);
              }
              break;
            case "documentType":
              if (Array.isArray(value)) {
                params.set(URL_PARAM_KEYS.documentType, value.join(","));
              }
              break;
            case "dateRange":
              if (typeof value === "object" && !Array.isArray(value)) {
                const dateRange = value as DateRange;
                // Clear existing date params first
                params.delete(URL_PARAM_KEYS.dateRangeStart);
                params.delete(URL_PARAM_KEYS.dateRangeEnd);
                params.delete(URL_PARAM_KEYS.dateRangePreset);

                // Keep the marker active
                params.set(URL_PARAM_KEYS.dateRangeActive, "1");

                if (dateRange.startDate) {
                  params.set(URL_PARAM_KEYS.dateRangeStart, dateRange.startDate.toISOString());
                }
                if (dateRange.endDate) {
                  params.set(URL_PARAM_KEYS.dateRangeEnd, dateRange.endDate.toISOString());
                }
                if (dateRange.preset) {
                  params.set(URL_PARAM_KEYS.dateRangePreset, dateRange.preset);
                }
              }
              break;
          }

          // Reset to page 1 when filters change
          params.delete("page");

          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);

        // Remove all filter params
        params.delete(URL_PARAM_KEYS.proNumber);
        params.delete(URL_PARAM_KEYS.shipmentId);
        params.delete(URL_PARAM_KEYS.documentType);
        params.delete(URL_PARAM_KEYS.dateRangeActive);
        params.delete(URL_PARAM_KEYS.dateRangeStart);
        params.delete(URL_PARAM_KEYS.dateRangeEnd);
        params.delete(URL_PARAM_KEYS.dateRangePreset);

        // Reset to page 1 when filters change
        params.delete("page");

        return params;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const buildQueryParams = useCallback((): DocumentFilters => {
    const params: DocumentFilters = {};

    for (const filter of filters) {
      switch (filter.type) {
        case "proNumber":
          if (typeof filter.value === "string" && filter.value.trim()) {
            // PRO number matching behavior:
            // - Single value: Uses partial matching (ILIKE) for easier searching
            //   e.g., "16243" matches "162431407", "162431408", etc.
            // - Multiple comma-separated values: Uses exact matching (IN clause)
            //   e.g., "162431407, 160124251" matches only those exact PRO numbers
            const proValues = filter.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            if (proValues.length > 1) {
              // Multiple exact PRO numbers
              params.proNumbers = proValues;
            } else {
              // Single value - use partial matching
              params.proNumberLike = filter.value.trim();
            }
          }
          break;

        case "shipmentId":
          if (typeof filter.value === "string") {
            const ids = filter.value
              .split(",")
              .map((s) => parseInt(s.trim(), 10))
              .filter((n) => !isNaN(n));
            if (ids.length === 1) {
              params.shipmentId = ids[0];
            } else if (ids.length > 1) {
              params.shipmentIds = ids;
            }
          }
          break;

        case "documentType":
          if (Array.isArray(filter.value)) {
            if (filter.value.length === 1) {
              params.documentType = filter.value[0];
            } else if (filter.value.length > 1) {
              params.documentTypes = filter.value;
            }
          } else if (typeof filter.value === "string") {
            params.documentType = filter.value;
          }
          break;

        case "dateRange":
          if (typeof filter.value === "object" && !Array.isArray(filter.value)) {
            const dateRange = filter.value as DateRange;
            if (dateRange.startDate) {
              params.startDate = dateRange.startDate.toISOString();
            }
            if (dateRange.endDate) {
              params.endDate = dateRange.endDate.toISOString();
            }
          }
          break;
      }
    }

    return params;
  }, [filters]);

  return {
    filters,
    addFilter,
    removeFilter,
    updateFilter,
    clearFilters,
    buildQueryParams,
  };
}

/**
 * Helper to create a PRO number filter
 */
export function createProNumberFilter(value: string): ActiveFilter {
  return {
    type: "proNumber",
    value,
    displayValue: value,
  };
}

/**
 * Helper to create a shipment ID filter
 */
export function createShipmentIdFilter(value: string): ActiveFilter {
  return {
    type: "shipmentId",
    value,
    displayValue: value,
  };
}

/**
 * Helper to create a document type filter
 */
export function createDocumentTypeFilter(types: string[]): ActiveFilter {
  return {
    type: "documentType",
    value: types,
    displayValue: types.length === 1 ? types[0] : `${types.length} types`,
  };
}

/**
 * Helper to create a date range filter
 */
export function createDateRangeFilter(dateRange: DateRange): ActiveFilter {
  return {
    type: "dateRange",
    value: dateRange,
    displayValue: formatDateRangeDisplay(dateRange),
  };
}

/**
 * Helper to create a date range from preset
 */
export function createDateRangeFromPreset(
  preset: "today" | "last7days" | "last30days" | "custom"
): DateRange {
  if (preset === "custom") {
    return { startDate: null, endDate: null, preset: "custom" };
  }

  const now = new Date();
  const endDate = new Date(now);
  endDate.setHours(23, 59, 59, 999);

  let startDate: Date;

  switch (preset) {
    case "today":
      startDate = new Date(now);
      startDate.setHours(0, 0, 0, 0);
      break;
    case "last7days":
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      break;
    case "last30days":
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      break;
  }

  return {
    startDate,
    endDate,
    preset,
  };
}
